import { createFileRoute } from '@tanstack/react-router'
import { Helmet } from 'react-helmet-async'
import * as s from '@/components/legal-styles'

export const Route = createFileRoute('/legal/acceptable-use')({
  component: AcceptableUsePage,
})

const tableStyle: React.CSSProperties = {
  width: '100%', borderCollapse: 'collapse', marginBottom: 24, fontSize: 14, lineHeight: 1.7,
}
const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '10px 14px', borderBottom: '2px solid #EDE9E4',
  fontWeight: 700, color: '#1a1216', fontSize: 13,
}
const tdStyle: React.CSSProperties = {
  padding: '10px 14px', borderBottom: '1px solid #EDE9E4', color: '#4a4745', fontSize: 14,
}

function AcceptableUsePage() {
  return (
    <div>
      <Helmet>
        <title>Acceptable Use Policy | One-Email</title>
        <meta name="description" content="One-Email Acceptable Use Policy. Consent-based sending rules, prohibited content, sending practices, ramp limits, and enforcement." />
        <link rel="canonical" href="https://one-email.com/legal/acceptable-use" />
      </Helmet>

      <h1 style={s.heading}>Acceptable Use Policy</h1>
      <p style={s.updated}>Last updated: March 24, 2026</p>

      <div style={s.highlight}>
        <strong>Summary:</strong> The line is consent, not content. You can send anything the recipient opted into receiving. If they didn&apos;t ask for it, don&apos;t send it. We monitor bounce rates, complaint rates, and sending patterns to protect every customer&apos;s deliverability.
      </div>

      {/* 1. The Consent Principle */}
      <h2 id="consent" style={s.h2}>1. The Consent Principle</h2>
      <p style={s.p}>
        One-Email draws the line at <strong>consent</strong>. We do not distinguish between &ldquo;transactional&rdquo; and &ldquo;marketing&rdquo; email for the purpose of deciding what is allowed. The only question is: <strong>did the recipient give you their email address and agree to receive this type of email?</strong>
      </p>
      <p style={s.p}>
        This applies to every email you send through our platform. If the answer is yes, send it. If the answer is no, or you&apos;re not sure, don&apos;t.
      </p>

      {/* 2. Sending Streams */}
      <h2 id="streams" style={s.h2}>2. Sending Streams</h2>
      <p style={s.p}>
        One-Email maintains <strong>separate infrastructure</strong> for transactional and marketing email. Each stream runs on separate IP addresses, separate SMTP servers, and separate queues. One never touches the other. This isolation protects your transactional email deliverability from being affected by marketing sending patterns.
      </p>
      <ul style={s.ul}>
        <li style={s.li}><strong>Transactional email:</strong> One-to-one messages triggered by a specific user action or system event (password resets, receipts, shipping notifications, account alerts, 2FA codes). Sent through the transactional stream.</li>
        <li style={s.li}><strong>Marketing email:</strong> One-to-many messages sent to groups of recipients (newsletters, promotions, product announcements, campaigns, drip sequences). Sent through the marketing stream.</li>
      </ul>
      <p style={s.p}>
        You must send each email type through the appropriate stream. <strong>Routing marketing email through the transactional stream is a violation of this policy</strong> and may result in suspension, because it endangers the transactional deliverability of every customer on the shared infrastructure.
      </p>

      {/* 3. Always Allowed */}
      <h2 id="always-allowed" style={s.h2}>3. Always Allowed</h2>
      <p style={s.p}>
        These are emails where the recipient directly initiated the interaction or has a clear expectation of receiving the message. No special conditions apply.
      </p>
      <ul style={s.ul}>
        <li style={s.li}>Password resets and magic link logins</li>
        <li style={s.li}>Two-factor authentication codes</li>
        <li style={s.li}>Purchase receipts, invoices, and order confirmations</li>
        <li style={s.li}>Shipping and delivery notifications</li>
        <li style={s.li}>Account notifications (usage alerts, billing changes, security warnings)</li>
        <li style={s.li}>Welcome emails immediately following account creation</li>
        <li style={s.li}>Deploy notifications and CI/CD alerts</li>
        <li style={s.li}>Comment, reply, and mention notifications</li>
        <li style={s.li}>Service status updates from a service the recipient signed up for</li>
      </ul>

      {/* 4. Allowed with Conditions */}
      <h2 id="allowed-conditions" style={s.h2}>4. Allowed with Conditions</h2>
      <p style={s.p}>
        These are emails where consent exists but must be verifiable. <strong>All three conditions</strong> below must be met.
      </p>
      <ul style={s.ul}>
        <li style={s.li}>Product changelog and &ldquo;what&apos;s new&rdquo; updates to existing active users</li>
        <li style={s.li}>Weekly or monthly digest emails to users who explicitly opted in</li>
        <li style={s.li}>Newsletters to your own opt-in subscriber list (the list must be self-built)</li>
        <li style={s.li}>Onboarding drip sequences to new signups (limited duration, directly following signup)</li>
        <li style={s.li}>Event reminders to registered attendees</li>
        <li style={s.li}>Re-engagement emails to existing users who have not unsubscribed</li>
      </ul>
      <p style={s.p}><strong>Conditions:</strong></p>
      <ol style={{ ...s.ul, listStyleType: 'decimal' }}>
        <li style={s.li}>Every email must include a working <strong>one-click unsubscribe</strong> mechanism (<code>List-Unsubscribe</code> and <code>List-Unsubscribe-Post</code> headers). If you use our marketing stream, we inject these automatically.</li>
        <li style={s.li}>You must be able to <strong>demonstrate opt-in</strong> if we ask. This means you can produce signup records (date, IP, method) for your recipients.</li>
        <li style={s.li}>If your bounce rate exceeds <strong>5%</strong> or your complaint rate exceeds <strong>0.1%</strong>, sending may be automatically paused regardless of claimed consent. These thresholds are consistent with industry standards and ISP expectations.</li>
      </ol>

      {/* 5. Never Allowed */}
      <h2 id="never-allowed" style={s.h2}>5. Never Allowed</h2>
      <p style={s.p}>These represent sending without consent. Violation may result in immediate suspension.</p>
      <ul style={s.ul}>
        <li style={s.li}><strong>Cold outreach</strong> to purchased, scraped, rented, borrowed, co-registration, appended, or harvested email lists</li>
        <li style={s.li}><strong>Emails to recipients who have unsubscribed</strong> or filed complaints</li>
        <li style={s.li}><strong>Affiliate or promotional blasts</strong> to non-customers</li>
        <li style={s.li}><strong>Any email to a list you did not build</strong> through direct opt-in</li>
        <li style={s.li}><strong>Phishing, spam, or deceptive content</strong> &mdash; including forged <code>From</code> headers, misleading subjects, or impersonation</li>
        <li style={s.li}><strong>Malware distribution</strong> or emails containing malicious attachments/links</li>
        <li style={s.li}><strong>Credential harvesting</strong> or emails designed to collect personal information through deception</li>
        <li style={s.li}><strong>Content that violates applicable laws</strong> (CAN-SPAM, CASL, GDPR, Spam Act 2003, LGPD)</li>
      </ul>

      {/* 6. Prohibited Content */}
      <h2 id="prohibited" style={s.h2}>6. Prohibited Content</h2>
      <p style={s.p}>Regardless of consent, the following content may not be sent through One-Email:</p>
      <ul style={s.ul}>
        <li style={s.li}>Child sexual abuse material (CSAM)</li>
        <li style={s.li}>Content promoting terrorism or extreme violence</li>
        <li style={s.li}>Illegal drugs or controlled substances</li>
        <li style={s.li}>Fraudulent schemes, pyramid schemes, or get-rich-quick offers</li>
        <li style={s.li}>Content that infringes intellectual property rights at scale (mass piracy distribution)</li>
        <li style={s.li}>Any content that is illegal in the sender&apos;s or recipient&apos;s jurisdiction</li>
      </ul>

      <h3 style={s.h3}>Age-Restricted Content</h3>
      <p style={s.p}>
        Content related to alcohol, tobacco, gambling, cannabis (where legal), firearms, or other age-restricted products or services may only be sent to recipients who are verified to be of legal age in their jurisdiction. You are responsible for age verification. If we detect age-restricted content being sent without reasonable age verification measures, we may suspend your account.
      </p>

      <h3 style={s.h3}>How We Handle Your Email Content</h3>
      <p style={s.p}>We want to be clear about how we interact with the emails you send:</p>
      <ul style={s.ul}>
        <li style={s.li}><strong>We do not store email content.</strong> Email bodies (HTML and plaintext) are processed in memory and discarded immediately after delivery. We never retain your email content.</li>
        <li style={s.li}><strong>We do not manually read your emails.</strong> No One-Email employee reads the content of emails you send through the platform.</li>
        <li style={s.li}><strong>We use automated abuse prevention.</strong> Our systems automatically monitor sending patterns, bounce rates, complaint rates, and other signals to detect abuse. This monitoring is pattern-based and metric-based &mdash; it does not involve human review of email content.</li>
        <li style={s.li}><strong>We may review content only in response to abuse reports.</strong> If we receive a complaint, spam report, or legal request about specific emails sent through our platform, we may review the relevant metadata (sender, recipient, subject hash, delivery status) to investigate. Even in these cases, email body content is not available to us because we do not store it.</li>
      </ul>

      {/* 7. Sending Practices */}
      <h2 id="practices" style={s.h2}>7. Sending Practices</h2>
      <ul style={s.ul}>
        <li style={s.li}>You must use a <strong>valid sender address</strong> that you control. Forged or misleading sender information is prohibited.</li>
        <li style={s.li}>Marketing emails must include a <strong>valid physical postal address</strong> (required by CAN-SPAM).</li>
        <li style={s.li}>You must <strong>honor unsubscribe requests</strong> within 10 business days (CAN-SPAM) or promptly (CASL, GDPR).</li>
        <li style={s.li}>You must maintain <strong>list hygiene</strong> &mdash; promptly remove hard bounces, honor complaints, and verify new subscribers.</li>
        <li style={s.li}>You must comply with <strong>CAN-SPAM</strong> (US), <strong>CASL</strong> (Canada), <strong>GDPR</strong> (EU/UK), <strong>Spam Act 2003</strong> (Australia), <strong>LGPD</strong> (Brazil), and all other applicable email regulations in the jurisdictions where your recipients are located.</li>
      </ul>

      {/* 8. Account Ramp Limits */}
      <h2 id="ramp" style={s.h2}>8. Account Ramp Limits</h2>
      <p style={s.p}>
        New accounts are subject to daily sending limits that increase over time. This protects our shared infrastructure and your deliverability while your account builds a sending reputation.
      </p>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Account Age</th>
              <th style={thStyle}>Daily Limit</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style={tdStyle}>Days 1-3</td><td style={tdStyle}>100 emails/day</td></tr>
            <tr><td style={tdStyle}>Days 4-7</td><td style={tdStyle}>500 emails/day</td></tr>
            <tr><td style={tdStyle}>Week 2 (Days 8-14)</td><td style={tdStyle}>2,000 emails/day</td></tr>
            <tr><td style={tdStyle}>Week 3+ (Day 15+)</td><td style={tdStyle}>Full tier limit</td></tr>
          </tbody>
        </table>
      </div>
      <p style={s.p}>
        Limits are automatically raised as your account ages. If you have a legitimate need for higher limits sooner, you can request early removal via the dashboard with a brief explanation of your use case.
      </p>
      <p style={s.p}>
        <strong>Anti-circumvention:</strong> You may not create or use multiple accounts to circumvent sending limits, ramp restrictions, rate limits, or any other safeguards. Attempting to evade enforcement through new account creation, credential sharing, or other means is a violation of this policy.
      </p>

      {/* 9. Monitoring and Enforcement */}
      <h2 id="monitoring" style={s.h2}>9. Monitoring and Enforcement</h2>
      <p style={s.p}>
        We monitor bounce rates, complaint rates, spam trap hits, and sending patterns across all accounts. This monitoring is automated and continuous &mdash; it protects every customer&apos;s deliverability by identifying problematic sending before it damages shared IP reputation.
      </p>
      <p style={s.p}>
        <strong>We reserve the right to monitor email content</strong> for the purpose of detecting abuse, enforcing this policy, and protecting our infrastructure. This is the legal basis for our abuse detection systems.
      </p>

      <h3 style={s.h3}>Enforcement Layers</h3>
      <p style={s.p}>Our enforcement operates in four layers:</p>
      <ol style={{ ...s.ul, listStyleType: 'decimal' }}>
        <li style={s.li}><strong>Automatic ramp limits</strong> for new accounts (Section 8 above).</li>
        <li style={s.li}><strong>Automated metric monitoring:</strong> If your bounce rate exceeds 3%, sending may be throttled. If it exceeds 5%, or if your complaint rate exceeds 0.1%, sending may be automatically paused. Spam trap hits (2+ in 7 days) trigger immediate pause and manual review.</li>
        <li style={s.li}><strong>Proactive signal detection:</strong> Accounts flagged for patterns that may indicate misuse (high freemail ratio, low open rates, unusual volume spikes) are flagged for review. Flags alone do not result in action.</li>
        <li style={s.li}><strong>Manual review:</strong> Accounts that accumulate multiple flags or trigger auto-pauses are reviewed by our team.</li>
      </ol>

      <h3 style={s.h3}>Consequences</h3>
      <p style={s.p}>We follow a graduated enforcement path:</p>
      <ol style={{ ...s.ul, listStyleType: 'decimal' }}>
        <li style={s.li}><strong>Warning:</strong> Email notification explaining the specific concern and guidance for improvement.</li>
        <li style={s.li}><strong>Throttling:</strong> Sending rate reduced to 50% while the issue is investigated.</li>
        <li style={s.li}><strong>Pause:</strong> Sending disabled. Queued emails are <strong>held, not dropped</strong>. You have 7 days to respond with an explanation. If resolved, sending resumes at a reduced rate, ramping back to full over 48 hours.</li>
        <li style={s.li}><strong>Suspension:</strong> Account access restricted. Queued emails continue to be held.</li>
        <li style={s.li}><strong>Termination:</strong> Permanent account closure. All queued emails dropped. DKIM keys revoked. Current billing period refunded. Reserved for egregious violations (phishing, malware, repeated violations after warnings) or cases where sending poses immediate risk to our infrastructure.</li>
      </ol>
      <p style={s.p}>
        <strong>For severe violations</strong> &mdash; phishing, malware distribution, spam trap hits, or sending that poses an immediate risk to our infrastructure or other customers &mdash; we may skip to pause, suspension, or termination <strong>without prior notice and at our sole discretion</strong>.
      </p>
      <p style={s.p}>
        <strong>No refunds</strong> are issued for any period during which your account is suspended or terminated due to a violation of this policy (except as stated above for termination).
      </p>

      {/* 10. Infrastructure Protection */}
      <h2 id="infrastructure" style={s.h2}>10. Infrastructure Protection</h2>
      <p style={s.p}>
        You must not use the Service in any manner that could damage, disable, overburden, or impair the Service, or interfere with any other customer&apos;s use. This includes but is not limited to:
      </p>
      <ul style={s.ul}>
        <li style={s.li}>Sending volumes that exceed your plan&apos;s limits without an active overage arrangement</li>
        <li style={s.li}>Attempting to bypass rate limiting mechanisms</li>
        <li style={s.li}>Engaging in any activity that degrades shared infrastructure performance</li>
      </ul>

      {/* 11. Reporting Abuse */}
      <h2 id="reporting" style={s.h2}>11. Reporting Abuse</h2>
      <p style={s.p}>
        If you receive unwanted email sent via One-Email, report it to <strong>abuse@one-email.com</strong>. We investigate all reports and take initial action within 24 hours.
      </p>
      <p style={s.p}>
        If you are a One-Email customer and believe your account was compromised, contact <strong>security@one-email.com</strong> immediately.
      </p>

      {/* 12. Changes to This Policy */}
      <h2 id="changes" style={s.h2}>12. Changes to This Policy</h2>
      <p style={s.p}>
        We may update this policy to reflect changes in our enforcement capabilities, industry standards, or legal requirements. Material changes will be communicated via email and dashboard notification at least <strong>30 days</strong> before taking effect.
      </p>
    </div>
  )
}
